import _ from "lodash";
import pLimit from "p-limit";

import { ReasonOrError } from "../ErrorUntyped";
import { iterateValues } from "../../../../Common/IterableUtils";
import { reject } from "../../../../Common/TypingUtils";
import { getCpuLimits } from "../GetCpuLimits";

import { AggregatedError } from "./AggregatedError";
import { DetachListener, IValueProducer, ValueListener } from "./BuildTask";

async function noop(): Promise<void> {
    // empty function
}

export abstract class ChainableValueProducer<T> implements IValueProducer<T> {
    private readonly errorListeners: Array<ValueListener<Error>> = [];

    public abstract onNext(_listener: (nextValue: T) => Promise<void>): DetachListener;

    public onError(listener: (error: Error) => Promise<void>): DetachListener {
        this.errorListeners.push(listener);
        return () => {
            const index = this.errorListeners.indexOf(listener);
            if (index >= 0) {
                this.errorListeners.splice(index, 1);
            }
        };
    }

    public get currentValue(): Promise<T> {
        throw new Error("AbstractError");
    }

    public transform<TO>(transformFunction: (input: T) => Promise<TO>): TransformValueProducer<T, TO> {
        return new TransformValueProducer(this, transformFunction);
    }

    public do(action: (input: T) => Promise<void>): ExecuteOnValueProducer<T> {
        return new ExecuteOnValueProducer(this, action);
    }

    public chainWith<TO, TVP extends IValueProducer<TO>>(nextItemFactory: (prevItem: IValueProducer<T>) => TVP): TVP {
        return nextItemFactory(this);
    }

    public attach<TA, TO = T>(
        attachFactory: (value: T) => Promise<IValueProducer<TA>>,
        joinValues?: (original: T, attached: TA) => Promise<TO>,
        evaluateAttachKey?: (value: T) => Promise<string>
    ): AttachValueProducer<T, TA, TO> {
        return new AttachValueProducer(this, attachFactory, joinValues, evaluateAttachKey);
    }

    public skipUpdatesUntilNextCompleted(): SkipUpdatesUntilNextCompletedProducer<T> {
        return new SkipUpdatesUntilNextCompletedProducer(this);
    }

    public splitToChains<TI, TO>(
        splitFunction: (input: T) => TI[],
        getKey: (input: TI) => string,
        factory: (input: TI) => IValueProducer<TO>
    ): SplitToChainsValueProducer<T, TI, TO> {
        return new SplitToChainsValueProducer(this, splitFunction, getKey, factory);
    }

    public splitToParallelChains<TI, TO>(
        splitFunction: (input: T) => TI[],
        getKey: (input: TI) => string,
        factory: (input: TI) => IValueProducer<TO> | Promise<IValueProducer<TO>>
    ): ParallelSplitToChainsValueProducer<T, TI, TO> {
        return new ParallelSplitToChainsValueProducer(this, splitFunction, getKey, factory);
    }

    public async observe(listener: ValueListener<T> = noop): Promise<() => Promise<void>> {
        await this.beginObserve();
        await listener(await this.currentValue);
        const detachListener = this.onNext(listener);
        return async () => {
            detachListener();
            await this.endObserve();
        };
    }

    public async beginObserve(): Promise<void> {
        throw new Error("AbstractError");
    }

    public async endObserve(): Promise<void> {
        throw new Error("AbstractError");
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected async emitError(error: any): Promise<void> {
        const errorObject = error instanceof Error ? error : new Error(`${error}`);
        for (const errorListener of this.errorListeners) {
            await errorListener(errorObject);
        }
    }
}

export class ObservableValueProducerBase<T> extends ChainableValueProducer<T> {
    public get currentValue(): Promise<T> {
        return this.getCurrentValue();
    }

    private readonly listeners: Array<ValueListener<T>> = [];

    protected async getCurrentValue(): Promise<T> {
        throw new Error("AbstractError");
    }

    public async beginObserve(): Promise<void> {
        await this.beginListening();
    }

    public async endObserve(): Promise<void> {
        await this.stopListening();
    }

    protected async beginListening(): Promise<void> {
        throw new Error("AbstractError");
    }

    protected async stopListening(): Promise<void> {
        throw new Error("AbstractError");
    }

    protected async emitUpdate(value: T): Promise<void> {
        await this.notifyAllListenersAndWaitForComplete(value);
    }

    public onNext(listener: (nextValue: T) => Promise<void>): DetachListener {
        this.listeners.push(listener);
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index >= 0) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private async notifyAllListenersAndWaitForComplete(nextValue: T): Promise<void> {
        for (const listener of this.listeners) {
            await listener(nextValue);
        }
    }
}

export class AttachValueProducer<TI, TA, TO> extends ChainableValueProducer<TO> {
    private readonly parent: IValueProducer<TI>;
    private readonly attachFactory: (value: TI) => Promise<IValueProducer<TA>>;
    private readonly joinValues: (original: TI, attached: TA) => Promise<TO>;
    private lastAttachKey?: string;
    private attachedProducer?: IValueProducer<TA>;
    private readonly evaluateAttachKey: (value: TI) => Promise<string>;
    private readonly listeners: Array<ValueListener<TO>> = [];
    private detachParentListener?: DetachListener;
    private detachAttachedListener?: DetachListener;
    private lastAttachedValue?: TA;
    private lastParentValue?: TI;
    private awaitingLastAttachedValue = false;
    private isInObserveState = false;

    public constructor(
        parent: IValueProducer<TI>,
        attachFactory: (value: TI) => Promise<IValueProducer<TA>>,
        joinValues?: (original: TI, attached: TA) => Promise<TO>,
        evaluateAttachKey?: (value: TI) => Promise<string>
    ) {
        super();
        this.parent = parent;
        this.attachFactory = attachFactory;
        // Я не могу победить типизацию в этом месте, слишком много у меня кода получается
        // @ts-ignore
        this.joinValues = joinValues || (async x => x);
        this.evaluateAttachKey = evaluateAttachKey || (async () => "STATIC-ATTACH-KEY");
    }

    public async beginObserve(): Promise<void> {
        this.isInObserveState = true;
        await this.parent.beginObserve();
    }

    public async endObserve(): Promise<void> {
        await this.parent.endObserve();
        if (this.attachedProducer != undefined) {
            await this.attachedProducer.endObserve();
        }
        this.isInObserveState = false;
    }

    public get currentValue(): Promise<TO> {
        const getCurrentValue = async (): Promise<TO> => {
            const parentValue = await this.parent.currentValue;
            this.lastParentValue = parentValue;
            const attachKey = await this.evaluateAttachKey(parentValue);
            if (this.attachedProducer != undefined && this.detachAttachedListener == undefined) {
                this.detachAttachedListener = this.attachedProducer.onNext(this.handleChangeAttachedValue);
            }
            if (this.lastAttachKey == undefined || this.lastAttachKey !== attachKey) {
                this.lastAttachKey = attachKey;
                if (this.detachAttachedListener != undefined) {
                    this.detachAttachedListener();
                }
                if (this.attachedProducer != undefined && this.isInObserveState) {
                    await this.attachedProducer.endObserve();
                }
                this.attachedProducer = await this.attachFactory(parentValue);
                if (this.isInObserveState) {
                    await this.attachedProducer.beginObserve();
                }
                this.detachAttachedListener = this.attachedProducer.onNext(this.handleChangeAttachedValue);
            }
            if (this.attachedProducer == undefined) {
                throw new Error("InvalidProgramState");
            }
            const attachedValue = await this.attachedProducer.currentValue;
            this.lastAttachedValue = attachedValue;
            return this.joinValues(parentValue, attachedValue);
        };
        return getCurrentValue();
    }

    public async emitNextValue(value: TO): Promise<void> {
        for (const listener of this.listeners) {
            await listener(value);
        }
    }

    public onNext(listener: (nextValue: TO) => Promise<void>): DetachListener {
        this.listeners.push(listener);
        if (this.listeners.length === 1) {
            if (this.detachParentListener == undefined) {
                this.detachParentListener = this.parent.onNext(this.handleChangeParentValue);
            }
            if (this.attachedProducer != undefined && this.detachParentListener == undefined) {
                this.detachParentListener = this.attachedProducer.onNext(this.handleChangeAttachedValue);
            }
        }
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index >= 0) {
                this.listeners.splice(index, 1);
            }
            if (this.listeners.length === 0) {
                if (this.detachParentListener != undefined) {
                    this.detachParentListener();
                }
                if (this.detachAttachedListener != undefined) {
                    this.detachAttachedListener();
                }
            }
        };
    }

    private readonly handleChangeAttachedValue = async (attachedValue: TA): Promise<void> => {
        this.lastAttachedValue = attachedValue;
        if (!this.awaitingLastAttachedValue) {
            if (this.lastParentValue == undefined) {
                throw new Error("InvalidProgramState");
            }
            await this.emitNextValue(await this.joinValues(this.lastParentValue, attachedValue));
        }
    };

    private readonly handleChangeParentValue = async (parentValue: TI): Promise<void> => {
        this.lastParentValue = parentValue;
        const attachKey = await this.evaluateAttachKey(parentValue);
        if (this.attachedProducer != undefined && this.detachAttachedListener == undefined) {
            this.detachAttachedListener = this.attachedProducer.onNext(this.handleChangeAttachedValue);
        }
        if (this.lastAttachKey == undefined || this.lastAttachKey !== attachKey) {
            this.lastAttachKey = attachKey;
            if (this.detachAttachedListener != undefined) {
                this.detachAttachedListener();
            }
            if (this.attachedProducer != undefined && this.isInObserveState) {
                await this.attachedProducer.endObserve();
            }
            this.attachedProducer = await this.attachFactory(parentValue);
            if (this.isInObserveState) {
                await this.attachedProducer.beginObserve();
            }
            this.detachAttachedListener = this.attachedProducer.onNext(this.handleChangeAttachedValue);
        }
        if (this.attachedProducer == undefined) {
            throw new Error("InvalidProgramState");
        }
        // Просто немного обождем, вдруг придёт другое attachedValue
        this.awaitingLastAttachedValue = true;
        await delay(700);
        this.awaitingLastAttachedValue = false;
        let attachedValue = this.lastAttachedValue;
        if (attachedValue == undefined) {
            attachedValue = await this.attachedProducer.currentValue;
        }
        await this.emitNextValue(await this.joinValues(parentValue, attachedValue));
    };
}

export class ExecuteOnValueProducer<T> extends ChainableValueProducer<T> {
    private readonly parent: IValueProducer<T>;
    private readonly action: (value: T) => Promise<void>;

    public constructor(parent: IValueProducer<T>, action: (value: T) => Promise<void>) {
        super();
        this.parent = parent;
        this.action = action;
    }

    public async beginObserve(): Promise<void> {
        await this.parent.beginObserve();
    }

    public async endObserve(): Promise<void> {
        await this.parent.endObserve();
    }

    public get currentValue(): Promise<T> {
        const getCurrentValue = async (): Promise<T> => {
            const value = await this.parent.currentValue;
            await this.action(value);
            return value;
        };
        return getCurrentValue();
    }

    public onNext(listener: (nextValue: T) => Promise<void>): DetachListener {
        return this.parent.onNext(async parentValue => {
            try {
                const value = parentValue;
                await this.action(value);
                await listener(value);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
                await this.emitError(e);
            }
        });
    }
}

export class TransformValueProducer<TI, TO> extends ChainableValueProducer<TO> {
    private readonly parent: IValueProducer<TI>;
    private readonly transformFunction: (value: TI) => Promise<TO>;

    public constructor(parent: IValueProducer<TI>, transformFunction: (value: TI) => Promise<TO>) {
        super();
        this.parent = parent;
        this.transformFunction = transformFunction;
    }

    public async beginObserve(): Promise<void> {
        await this.parent.beginObserve();
    }

    public async endObserve(): Promise<void> {
        await this.parent.endObserve();
    }

    public get currentValue(): Promise<TO> {
        const getCurrentValue = async (): Promise<TO> => this.transformFunction(await this.parent.currentValue);
        return getCurrentValue();
    }

    public onNext(listener: (nextValue: TO) => Promise<void>): DetachListener {
        return this.parent.onNext(async parentValue => {
            try {
                await listener(await this.transformFunction(parentValue));
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                await this.emitError(e);
            }
        });
    }
}

export class SplitToChainsValueProducer<T, TI, TO> extends ChainableValueProducer<TO[]> {
    private readonly parent: IValueProducer<T>;
    private readonly splitFunction: (input: T) => TI[];
    private readonly getKey: (input: TI) => string;
    private readonly factory: (input: TI) => IValueProducer<TO>;
    private readonly listeners: Array<ValueListener<TO[]>> = [];
    private readonly chains: {
        [key: string]: {
            valueProducer: IValueProducer<TO>;
            lastValue: undefined | TO;
            detachListener: DetachListener;
        };
    } = {};
    private isInObserveState = false;

    public constructor(
        parent: IValueProducer<T>,
        splitFunction: (input: T) => TI[],
        getKey: (input: TI) => string,
        factory: (input: TI) => IValueProducer<TO>
    ) {
        super();
        this.parent = parent;
        this.splitFunction = splitFunction;
        this.getKey = getKey;
        this.factory = factory;
        this.parent.onNext(this.handleChangeParent);
    }

    public get currentValue(): Promise<TO[]> {
        const getCurrentValue = async (): Promise<TO[]> => {
            const values = this.splitFunction(await this.parent.currentValue);
            const result: TO[] = [];
            const errors: ReasonOrError[] = [];
            for (const value of values) {
                const childValueProducer = this.factory(value);
                if (this.isInObserveState) {
                    await childValueProducer.beginObserve();
                }
                try {
                    const childValue = await childValueProducer.currentValue;
                    result.push(childValue);
                    const key = this.getKey(value);
                    this.chains[key] = {
                        lastValue: childValue,
                        valueProducer: childValueProducer,
                        detachListener: childValueProducer.onNext(x => this.handleChangeParentChain(x, key)),
                    };
                } catch (e) {
                    if (this.isInObserveState) {
                        // eslint-disable-next-line no-console
                        console.log(e);
                    } else {
                        errors.push(e);
                    }
                    const key = this.getKey(value);
                    this.chains[key] = {
                        lastValue: undefined,
                        valueProducer: childValueProducer,
                        detachListener: childValueProducer.onNext(x => this.handleChangeParentChain(x, key)),
                    };
                }
            }
            if (errors.length === 1) {
                throw errors[0];
            } else if (errors.length > 1) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                throw new AggregatedError("There are errors", ...errors);
            }
            return result;
        };
        return getCurrentValue();
    }

    public async beginObserve(): Promise<void> {
        this.isInObserveState = true;
        await this.parent.beginObserve();
    }

    public async endObserve(): Promise<void> {
        await this.parent.endObserve();
        for (const value of iterateValues(this.chains)) {
            await value.valueProducer.endObserve();
        }
        this.isInObserveState = false;
    }

    public onNext(listener: (nextValue: TO[]) => Promise<void>): DetachListener {
        this.listeners.push(listener);
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index >= 0) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private async emitValue(value: TO[]): Promise<void> {
        for (const listener of this.listeners) {
            await listener(value);
        }
    }

    private toMap(values: TI[]): { [key: string]: TI } {
        const result: { [key: string]: TI } = {};
        for (const value of values) {
            result[this.getKey(value)] = value;
        }
        return result;
    }

    private readonly handleChangeParentChain = async (nextValue: TO, key: string): Promise<void> => {
        const chainValue = this.chains[key];
        if (chainValue != undefined) {
            chainValue.lastValue = nextValue;
        }
        await this.emitValue(
            Object.values(this.chains)
                .map(x => x.lastValue)
                .filter(isNotNullOrUndefined)
        );
    };

    private readonly handleChangeParent = async (input: T): Promise<void> => {
        const values = this.splitFunction(input);
        const nextValuesMap = this.toMap(values);

        const newKeys = _.difference(Object.keys(nextValuesMap), Object.keys(this.chains));
        const removedKeys = _.difference(Object.keys(this.chains), Object.keys(nextValuesMap));
        for (const newKey of newKeys) {
            const newChain = this.factory(nextValuesMap[newKey] ?? reject());
            if (this.isInObserveState) {
                await newChain.beginObserve();
            }
            try {
                const newChainValue = await newChain.currentValue;
                this.chains[newKey] = {
                    lastValue: newChainValue,
                    valueProducer: newChain,
                    detachListener: newChain.onNext(nextValue => this.handleChangeParentChain(nextValue, newKey)),
                };
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
                this.chains[newKey] = {
                    lastValue: undefined,
                    valueProducer: newChain,
                    detachListener: newChain.onNext(nextValue => this.handleChangeParentChain(nextValue, newKey)),
                };
            }
        }
        for (const removedKey of removedKeys) {
            const removedChain = this.chains[removedKey];
            removedChain?.detachListener();
            if (this.isInObserveState) {
                await removedChain?.valueProducer.endObserve();
            }
            // Я не знаю, как тут правильно
            delete this.chains[removedKey];
        }
        await this.emitValue(
            Object.values(this.chains)
                .map(x => x.lastValue)
                .filter(isNotNullOrUndefined)
        );
    };
}

export class ParallelSplitToChainsValueProducer<T, TI, TO> extends ChainableValueProducer<TO[]> {
    private readonly parent: IValueProducer<T>;
    private readonly splitFunction: (input: T) => TI[];
    private readonly getKey: (input: TI) => string;
    private readonly factory: (input: TI) => IValueProducer<TO> | Promise<IValueProducer<TO>>;
    private readonly listeners: Array<ValueListener<TO[]>> = [];
    private readonly chains: {
        [key: string]: {
            valueProducer: IValueProducer<TO>;
            lastValue: undefined | TO;
            detachListener: DetachListener;
        };
    } = {};
    private isInObserveState = false;

    public constructor(
        parent: IValueProducer<T>,
        splitFunction: (input: T) => TI[],
        getKey: (input: TI) => string,
        factory: (input: TI) => IValueProducer<TO> | Promise<IValueProducer<TO>>
    ) {
        super();
        this.parent = parent;
        this.splitFunction = splitFunction;
        this.getKey = getKey;
        this.factory = factory;
        this.parent.onNext(this.handleChangeParent);
    }

    public get currentValue(): Promise<TO[]> {
        const getCurrentValue = async (): Promise<TO[]> => {
            const values = this.splitFunction(await this.parent.currentValue);
            const result: TO[] = [];
            const errors: ReasonOrError[] = [];
            const cpuLimit = getCpuLimits();
            // eslint-disable-next-line no-console
            console.log(`Use limit: ${cpuLimit}`);
            const concurrencyLimit = pLimit(cpuLimit);
            await Promise.all(
                values.map((value, index) =>
                    concurrencyLimit(async () => {
                        const childValueProducer = await this.factory(value);
                        if (this.isInObserveState) {
                            await childValueProducer.beginObserve();
                        }
                        try {
                            const childValue = await childValueProducer.currentValue;
                            result[index] = childValue;
                            const key = this.getKey(value);
                            this.chains[key] = {
                                lastValue: childValue,
                                valueProducer: childValueProducer,
                                detachListener: childValueProducer.onNext(x => this.handleChangeParentChain(x, key)),
                            };
                        } catch (e) {
                            if (this.isInObserveState) {
                                // eslint-disable-next-line no-console
                                console.log(e);
                            } else {
                                errors.push(e);
                            }
                            const key = this.getKey(value);
                            this.chains[key] = {
                                lastValue: undefined,
                                valueProducer: childValueProducer,
                                detachListener: childValueProducer.onNext(x => this.handleChangeParentChain(x, key)),
                            };
                        }
                    })
                )
            );
            if (errors.length === 1) {
                throw errors[0];
            } else if (errors.length > 1) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                throw new AggregatedError("There are errors", ...errors);
            }
            return result;
        };
        return getCurrentValue();
    }

    public async beginObserve(): Promise<void> {
        this.isInObserveState = true;
        await this.parent.beginObserve();
    }

    public async endObserve(): Promise<void> {
        await this.parent.endObserve();
        for (const key of Object.keys(this.chains)) {
            await this.chains[key]?.valueProducer.endObserve();
        }
        this.isInObserveState = false;
    }

    public onNext(listener: (nextValue: TO[]) => Promise<void>): DetachListener {
        this.listeners.push(listener);
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index >= 0) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private async emitValue(value: TO[]): Promise<void> {
        for (const listener of this.listeners) {
            await listener(value);
        }
    }

    private toMap(values: TI[]): { [key: string]: TI } {
        const result: { [key: string]: TI } = {};
        for (const value of values) {
            result[this.getKey(value)] = value;
        }
        return result;
    }

    private readonly handleChangeParentChain = async (nextValue: TO, key: string): Promise<void> => {
        const chainValue = this.chains[key];
        if (chainValue != undefined) {
            chainValue.lastValue = nextValue;
        }
        await this.emitValue(
            Object.values(this.chains)
                .map(x => x.lastValue)
                .filter(isNotNullOrUndefined)
        );
    };

    private readonly handleChangeParent = async (input: T): Promise<void> => {
        const values = this.splitFunction(input);
        const nextValuesMap = this.toMap(values);

        const newKeys = _.difference(Object.keys(nextValuesMap), Object.keys(this.chains));
        const removedKeys = _.difference(Object.keys(this.chains), Object.keys(nextValuesMap));
        for (const newKey of newKeys) {
            const newChain = await this.factory(nextValuesMap[newKey] ?? reject());
            if (this.isInObserveState) {
                await newChain.beginObserve();
            }
            try {
                const newChainValue = await newChain.currentValue;
                this.chains[newKey] = {
                    lastValue: newChainValue,
                    valueProducer: newChain,
                    detachListener: newChain.onNext(nextValue => this.handleChangeParentChain(nextValue, newKey)),
                };
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
                this.chains[newKey] = {
                    lastValue: undefined,
                    valueProducer: newChain,
                    detachListener: newChain.onNext(nextValue => this.handleChangeParentChain(nextValue, newKey)),
                };
            }
        }
        for (const removedKey of removedKeys) {
            const removedChain = this.chains[removedKey];
            removedChain?.detachListener();
            if (this.isInObserveState) {
                await removedChain?.valueProducer.endObserve();
            }
            // Я не знаю, как тут правильно
            delete this.chains[removedKey];
        }
        await this.emitValue(
            Object.values(this.chains)
                .map(x => x.lastValue)
                .filter(isNotNullOrUndefined)
        );
    };
}

export class SkipUpdatesUntilNextCompletedProducer<T> extends ObservableValueProducerBase<T> {
    private readonly parent: IValueProducer<T>;
    private detachFromParent?: DetachListener;
    private lastParentValue?: T;

    private nextEmitToProcess?: () => Promise<void>;
    private currentlyProcessingEmit?: Promise<void>;

    public constructor(parent: IValueProducer<T>) {
        super();
        this.parent = parent;
    }

    protected async getCurrentValue(): Promise<T> {
        if (this.lastParentValue == undefined) {
            this.currentlyProcessingEmit = (async () => {
                this.lastParentValue = await this.parent.currentValue;
            })();
            await this.currentlyProcessingEmit;
        }
        if (this.lastParentValue == undefined) {
            throw new Error("InvalidProgramState");
        }
        return this.lastParentValue;
    }

    protected async beginListening(): Promise<void> {
        await this.parent.beginObserve();
        if (this.detachFromParent == undefined) {
            this.detachFromParent = this.parent.onNext(nextValue => this.handleChangeParentValue(nextValue));
        }
    }

    protected async stopListening(): Promise<void> {
        if (this.detachFromParent != undefined) {
            this.detachFromParent();
        }
        await this.parent.endObserve();
    }

    private async handleChangeParentValue(nextValue: T): Promise<void> {
        this.lastParentValue = nextValue;
        this.nextEmitToProcess = _.once(() => this.emitUpdate(nextValue));
        if (this.currentlyProcessingEmit != undefined) {
            await this.currentlyProcessingEmit;
        }
        this.currentlyProcessingEmit = this.nextEmitToProcess();
        await this.currentlyProcessingEmit;
        this.currentlyProcessingEmit = undefined;
    }
}

function delay(timeout: number): Promise<void> {
    return new Promise<void>(resolve => {
        setTimeout(() => {
            resolve();
        }, timeout);
    });
}

function isNotNullOrUndefined<T>(input: T | undefined | null): input is T {
    return input != undefined;
}
